import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { selectCurrentTheme } from '@interacta-shared/data-access-theme';
import {
    filterMap,
    Index,
    isDefined,
    lastLoading,
    objectIsEmpty,
    uuid,
} from '@interacta-shared/util';
import { ThemeMode, wrapWithSpan } from '@interacta-shared/util-common';
import {
    AttachmentCategoryType,
    IAttachment,
    IListAttachments,
    isMediaAttachment,
} from '@interacta/util-post';
import {
    AIContextType,
    AIMessage,
    HistoryMap,
    MAIN_THREAD,
} from '@modules/ai/models/ai.model';
import { AIActions } from '@modules/ai/store/ai.actions';
import { AIContextState } from '@modules/ai/store/ai.reducer';
import * as AISelectors from '@modules/ai/store/ai.selectors';
import { selectActiveContext } from '@modules/ai/store/ai.selectors';
import { selectPostById } from '@modules/communities/store/post/post.selectors';
import { computePostAttachmentDetailCounters } from '@modules/post/utils/post.utils';
import { GalleryStateService } from '@modules/state/services/gallery-state.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    BehaviorSubject,
    combineLatest,
    concat,
    concatMap,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    Observable,
    of,
    share,
    Subject,
    switchMap,
    takeUntil,
    tap,
    timer,
} from 'rxjs';
import { AIPanelTipType } from '../ai-panel-tip/ai-panel-tip.component';
import { AISkeletonLineWidth } from '../ai-skeleton-text/ai-skeleton-text.component';

@Component({
    selector: 'interacta-ai-panel-chat',
    templateUrl: './ai-panel-chat.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AIPanelChatComponent implements OnChanges, OnDestroy {
    private readonly galleryStateService = inject(GalleryStateService);
    private readonly translateService = inject(TranslateService);
    private readonly store = inject(Store);

    @Input({ required: true }) threadId!: Index;
    @Input() threadName: string | null = null;
    @Input() aiPostAttachmentEnabled = false;

    @ViewChild('input') input!: ElementRef<HTMLInputElement>;
    @ViewChild('scrollContainer')
    scrollContainer?: ElementRef<HTMLDivElement>;

    readonly loadingSkeletonLines: AISkeletonLineWidth[] = [
        'full',
        'half',
        'two-thirds',
    ];

    readonly context$: Observable<AIContextState>;

    readonly theme$: Observable<ThemeMode>;
    readonly focusOnInput$: Observable<boolean>;
    readonly isFetching$: Observable<boolean>;
    readonly sessionId$: Observable<number>;
    readonly canAddAttachment$: Observable<boolean>;

    contextId$ = new BehaviorSubject<number | null>(null);
    contextType$ = new BehaviorSubject<AIContextType | null>(null);
    threadId$ = new BehaviorSubject<Index | null>(null);
    historyMap$ = new BehaviorSubject<HistoryMap | null>(null);
    triggerFocus$ = new BehaviorSubject(false);
    destroy$ = new Subject<void>();

    questionControl = new FormControl<string>('', { nonNullable: true });

    readonly MAIN_THREAD = MAIN_THREAD;
    readonly AIContextType = AIContextType;
    readonly AIPanelTipType = AIPanelTipType;
    readonly wrapWithSpan = wrapWithSpan;
    readonly objectIsEmpty = objectIsEmpty;

    constructor() {
        this.theme$ = this.store
            .select(selectCurrentTheme)
            .pipe(filterMap((theme) => theme.mode));

        this.context$ = this.store.select(selectActiveContext).pipe(
            filter(isDefined),
            tap(({ contextId, contextType }) => {
                this.contextId$.next(contextId);
                this.contextType$.next(contextType);
            }),
        );

        this.isFetching$ = this.threadId$.pipe(
            filter(isDefined),
            switchMap((threadId) =>
                this.store.select(AISelectors.selectActiveIsFetching(threadId)),
            ),
        );

        this.sessionId$ = this.store
            .select(AISelectors.selectActiveSessionId)
            .pipe(filter(isDefined));

        this.focusOnInput$ = this.triggerFocus$.pipe(
            concatMap((focus) =>
                concat(of(focus), timer(1200).pipe(map(() => false))),
            ),
        );

        const activeThread$ = combineLatest([
            this.threadId$,
            this.store.select(AISelectors.selectActiveThread),
        ]).pipe(
            filterMap(([threadId, activeThread]) =>
                threadId === activeThread ? threadId : undefined,
            ),
            distinctUntilChanged(),
            takeUntil(this.destroy$),
            share(),
        );

        activeThread$
            .pipe(
                mergeMap((threadId) =>
                    this.store
                        .select(
                            AISelectors.selectActiveHistory(
                                threadId ?? undefined,
                            ),
                        )
                        .pipe(
                            map((history) => ({
                                threadId,
                                history,
                            })),
                        ),
                ),
            )
            .subscribe(({ history, threadId }) => {
                if (this.threadId$.value === threadId)
                    this.historyMap$.next(history?.history ?? null);
            });

        activeThread$
            .pipe(
                mergeMap((id) =>
                    this.store.select(
                        AISelectors.selectActiveDraftQuestion(id ?? undefined),
                    ),
                ),
            )
            .subscribe((value) => {
                if (value) this.questionControl.setValue(value);
                else this.questionControl.reset();
            });

        this.canAddAttachment$ = combineLatest([
            this.context$,
            this.threadId$,
        ]).pipe(
            filter(isDefined),
            switchMap(([context, thread]) => {
                if (
                    this.aiPostAttachmentEnabled &&
                    context.contextId &&
                    context.contextType === AIContextType.POST &&
                    thread === MAIN_THREAD
                ) {
                    return this.store
                        .select(selectPostById(context.contextId))
                        .pipe(
                            map((post) => {
                                if (!post) return false;

                                const counters =
                                    computePostAttachmentDetailCounters(
                                        post,
                                        context.workflowEnabled ?? false,
                                    );

                                return (
                                    counters.mediasCount +
                                        counters.documentsCount >
                                    0
                                );
                            }),
                        );
                } else return of(false);
            }),
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes?.threadId &&
            changes?.threadId?.previousValue !== changes?.threadId?.currentValue
        ) {
            this.threadId$.next(this.threadId);
        }
    }

    ngOnDestroy(): void {
        this.saveDraft();
        this.destroy$.next();
    }

    scrollToBottom(): void {
        setTimeout(() => {
            this.scrollContainer?.nativeElement.scrollTo({
                top: this.scrollContainer.nativeElement.scrollHeight + 100,
            });
        });
    }

    getHistoryArray(history: HistoryMap): { id: string; value: AIMessage }[] {
        const list: { id: string; value: AIMessage }[] = [];
        Object.entries(history).forEach(([key, value]) => {
            list.push({
                id: key,
                value: { ...value },
            });
        });
        return list;
    }

    sendRequestToAI(sessionId: number | null, question?: string): void {
        const contextId = this.contextId$.getValue();
        const contextType = this.contextType$.getValue();
        if (
            (question || this.questionControl.value.trim().length) &&
            contextId &&
            contextType
        ) {
            this.store.dispatch(
                AIActions.getResponseFromAI({
                    contextId,
                    contextType,
                    question: question ?? this.questionControl.value,
                    sessionId,
                    timestamp: new Date(),
                    clientId: uuid(),
                    threadId: this.threadId,
                    attachmentId:
                        this.threadId === MAIN_THREAD
                            ? undefined
                            : this.threadId,
                }),
            );
            this.questionControl.reset();
        }
    }

    retry(message: string): void {
        this.sendRequestToAI(null, message);
    }

    getResume(question: string): void {
        this.sendRequestToAI(null, this.translateService.instant(question));
    }

    startNewConversation(
        requestMessage: string,
        responseMessage: string,
    ): void {
        const contextId = this.contextId$.getValue();
        const contextType = this.contextType$.getValue();
        if (contextId && contextType) {
            this.store.dispatch(
                AIActions.startNewConversation({
                    contextId,
                    contextType,
                    requestMessage:
                        this.translateService.instant(requestMessage),
                    responseMessage:
                        this.translateService.instant(responseMessage),
                }),
            );
        }
        this.input.nativeElement.focus();
        this.triggerFocus$.next(true);
    }

    openAttachmentPreview(attachment: IAttachment): void {
        const attachmentList: IListAttachments = {
            attachments: [attachment],
            pageTokenInfo: lastLoading(),
            totalCount: 1,
            isLoading: false,
            isError: false,
            attachmentCategoryType: isMediaAttachment(attachment)
                ? AttachmentCategoryType.MULTIMEDIA
                : AttachmentCategoryType.DOCUMENT,
        };

        this.galleryStateService.openGallery(
            {
                feature: 'embedded',
                attachmentList,
            },
            0,
            undefined,
            undefined,
            true,
        );
    }

    goToThread(index?: Index): void {
        this.saveDraft();
        this.store.dispatch(
            AIActions.setActiveThreadInActiveContext({
                threadId: index ?? MAIN_THREAD,
            }),
        );
    }

    private saveDraft(): void {
        const contextId = this.contextId$.getValue();
        const contextType = this.contextType$.getValue();
        if (this.questionControl.value && contextId && contextType) {
            this.store.dispatch(
                AIActions.saveDraftQuestion({
                    contextId,
                    contextType,
                    draftQuestion: this.questionControl.value,
                    threadId: this.threadId,
                }),
            );
        }
    }
}
